import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as CalenderIcon } from '../../../../assets/icons/calender-v2-icon.svg';
import { ReactComponent as CarIcon } from '../../../../assets/icons/car.svg';
import { ReactComponent as CardPosIcon } from '../../../../assets/icons/card-pos-icon.svg';
import { ReactComponent as CardIcon } from '../../../../assets/icons/credit-card.svg';
import { ReactComponent as FleshIcon } from '../../../../assets/icons/flash-icon.svg';
import { ReactComponent as ChargerIcon } from '../../../../assets/icons/gas-icon.svg';
import { ReactComponent as NavigationArrowIcon } from '../../../../assets/icons/navigate-back.svg';
import { ReactComponent as RfidIcon } from '../../../../assets/icons/rfid-icon.svg';
import { ReactComponent as FavoriteIcon } from '../../../../assets/icons/save-bookmark-icon.svg';
import { ReactComponent as TimerIcon } from '../../../../assets/icons/timer-icon.svg';
import { brandText } from '../../../../config/branding';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchBasicInformations,
  setEndConsumerData,
  setSelectedEndConsumer,
} from '../../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
// import CustomRadioButton from '../../../../shared-components/custom-radio-button/CustomRadioButton';
import TabSwitchList, {
  TabItem,
} from '../../../../shared-components/tab-switch-list/TabSwitchList';
import MapSidePanel from '../../../../shared-components/table-side-panel/MapSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import TablesToggle from '../../../../shared-components/tables-toggle/TablesToggle';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import formatCardDisplay from '../../../../shared-functions/FormatCardDisplay';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';
import rfidApi from '../../../../utils/api/rfid';
import endConsumerV2Api from '../../../../utils/api/v2/end-consumer';
import SessionsCountChart from '../ChartComponents/ProfileChart/SessionsCountChart';
import SpendChart from '../ChartComponents/ProfileChart/SpendChart';
import TotalConsumptionChart from '../ChartComponents/ProfileChart/TotalConsumptionChart';
import ActiveChargingSession from '../SidePaneComponents/ActiveChargingSession/ActiveChargingSession';
import MyVehicle from '../SidePaneComponents/MyVehicle/MyVehicle';
import PaymentMethod from '../SidePaneComponents/PaymentMethod/PaymentMethod';
import RfidCard from '../SidePaneComponents/RfidCard/RfidCard';

import ChargeSessionCommercialList from './ChargeSessionCommercialList';
import ChargeSessionPersonalList from './ChargeSessionPersonalList';
import GuestChargingList from './GuestChargingList';
// import InvoiceList from './InvoiceList';
import InvoiceList from './InvoiceList';
import PaymentHistoryList from './PaymentHistoryList';
import PersonalChargers from './PersonalCharger';
import ProfileConsumer from './ProfileConsumer';
import ReservationsList from './ReservationsList';
import ScheduleCompletedList from './ScheduleCompletedList';
import ScheduleUpcomingList from './ScheduleUpcomingList';
import TransactionHistoryList from './TransactionHistoryList';

interface NotificationItem {
  title: string;
  createdAt: string;
}

interface OtpItemInterface {
  createdAt: string;
  otp: string;
  otpExpiresAt: string;
  type: string;
  updatedAt: string;
  userId: string;
}

/** Response shape for the driver-power-spend-stats endpoint */
interface DriverPowerSpendStats {
  driverId: string;
  totalSessions?: number;
  totalSessionCount?: number;
  totalPowerConsumedKw?: number;
  totalPowerConsumedKwh?: number;
  totalEnergyDelivered?: number;
  spendAmount: Array<{ currency: string; amount: number }>;
  totalSpend: number;
  timePeriod: string | null;
  startDate: string | null;
  endDate: string | null;
  totalDuration?: number | string;
  totalDurationInMinutes?: number;
  totalDurationSeconds?: number;
  duration?: number | string;
  durationSeconds?: number;
}

/**
 * Helper to format lifetime duration into a clean display string
 * @param {number | string | undefined | null} durationValue - Raw duration in seconds or minutes
 * @param {number | undefined | null} [minutesValue] - Optional explicit duration in minutes
 * @returns {string} Formatted duration string, e.g. "2h 45m 30s" or "35m 10s"
 */
const formatLifetimeDuration = (
  durationValue: number | string | undefined | null,
  minutesValue?: number | null,
): string => {
  if (minutesValue !== undefined && minutesValue !== null && minutesValue > 0) {
    const totalSeconds = Math.round(minutesValue * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    return parts.join(' ');
  }

  if (!durationValue || durationValue === 0 || durationValue === '0') {
    return '0s';
  }

  if (typeof durationValue === 'string') {
    if (
      durationValue.includes('h') ||
      durationValue.includes('m') ||
      durationValue.includes('s') ||
      durationValue.includes(':')
    ) {
      return durationValue;
    }
  }

  const numericValue = Number(durationValue);
  if (Number.isNaN(numericValue) || numericValue <= 0) {
    return '0s';
  }

  const totalSeconds = Math.round(numericValue);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(' ');
};

/**
 * EndConsumers Profile
 * @returns {any} - The EndConsumersProfile component
 */
function EndConsumersProfile() {
  const { t, i18n } = useTranslation('endConsumer');
  const { selectedEndConsumer, basicInformation } = useAppSelector(
    (state) => state.endConsumerType,
  );
  const dispatch = useAppDispatch();
  const { type } = useParams<{ type: string }>();
  const { userCustomId } = useParams<{ userCustomId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = useLocation();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [otps, setOtps] = useState<OtpItemInterface[]>([]);
  const [driverStats, setDriverStats] = useState<DriverPowerSpendStats | null>(null);
  const [favoriteStationCount, setFavoriteStationCount] = useState<number>(0);
  const [rfidCardsCount, setRfidCardsCount] = useState<number>(0);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
  const [sidePanelData, setSidePanelData] = useState<{
    title: string;
    content: React.ReactElement;
    width?: string | number;
  }>({
    title: '',
    content: <div />,
  });
  const [leftActiveTab, setLeftActiveTab] = useState('basicInfo');
  const [rightActiveTab, setRightActiveTab] = useState('lifetime');
  const [bottomActiveTab, setBottomActiveTab] = useState('activeSession');
  // const [consumerType, setConsumerType] = useState(type || 'commercial');
  const [consumerType] = useState('commercial');

  const leftContainerTabs: TabItem[] = [
    {
      label: t('basicInformation'),
      key: 'basicInfo',
      isActive: leftActiveTab === 'basicInfo',
    },
    {
      label: t('notifications'),
      key: 'notifications',
      isActive: leftActiveTab === 'notifications',
    },
    {
      label: t('otps'),
      key: 'otps',
      isActive: leftActiveTab === 'otps',
    },
  ];

  const rightContainerTabs: TabItem[] = [
    {
      label: t('lifetime'),
      key: 'lifetime',
      isActive: rightActiveTab === 'lifetime',
    },
    {
      label: t('totalConsumption'),
      key: 'totalConsumption',
      isActive: rightActiveTab === 'totalConsumption',
    },
    {
      label: t('spend'),
      key: 'spend',
      isActive: rightActiveTab === 'spend',
    },
    {
      label: t('sessionsCount'),
      key: 'sessionsCount',
      isActive: rightActiveTab === 'sessionsCount',
    },
  ];

  let statsData = driverStats;
  const hasTopLevelStats =
    driverStats &&
    !Array.isArray(driverStats) &&
    ('totalPowerConsumedKw' in driverStats || 'totalSessions' in driverStats);

  if (!hasTopLevelStats && (driverStats as any)?.data && !Array.isArray((driverStats as any)?.data)) {
    statsData = (driverStats as any)?.data;
  }
  const spendList = statsData?.spendAmount;

  const spendCards =
    Array.isArray(spendList) && spendList.length > 0
      ? spendList.map((item, index) => ({
        key: `spend-${item.currency || index}`,
        label:
          spendList.length > 1
            ? `${t('lifetimeSpend')} (${item.currency})`
            : t('lifetimeSpend'),
        value: ToFixedValue(Number(item.amount || 0)),
        unit: item.currency,
        icon: <CardPosIcon />,
        theme: 'spend',
      }))
      : [
        {
          key: 'spend',
          label: t('lifetimeSpend'),
          value: ToFixedValue(statsData?.totalSpend) || '0',
          unit: brandText.currency,
          icon: <CardPosIcon />,
          theme: 'spend',
        },
      ];

  const totalRfidCount = rfidCardsCount || basicInformation?.rfidCard?.length || 0;
  const personalChargerCount = basicInformation?.personalChargingPoints?.length || 0;
  const totalFavoriteCount =
    favoriteStationCount || basicInformation?.favouriteLocations?.length || 0;


  const cardsList = basicInformation?.cards || [];
  let paymentMethodDisplay = '';
  if (basicInformation?.preferredPaymentMethod === 'wallet') {
    paymentMethodDisplay = t('wallet', 'Wallet');
  } else {
    const defaultCard = cardsList.find((cardItem: any) => Boolean(cardItem.isDefault || cardItem.isSelected));
    if (defaultCard) {
      paymentMethodDisplay = formatCardDisplay(defaultCard, t('card', 'Card'));
    } else if (basicInformation?.hyperpayCustomerCard) {
      paymentMethodDisplay = basicInformation.hyperpayCustomerCard;
    } else if (cardsList.length > 0) {
      const firstCard = cardsList[0];
      paymentMethodDisplay = formatCardDisplay(firstCard, t('card', 'Card'));
    } else {
      paymentMethodDisplay = t('wallet', 'Wallet');
    }
  }
  const paymentExtraCount = cardsList.length > 1 ? cardsList.length - 1 : 0;

  const vehiclesList = basicInformation?.vehicles || [];
  const firstVehicleName =
    vehiclesList.length > 0
      ? vehiclesList[0].manufacturer ||
      vehiclesList[0].model ||
      vehiclesList[0].carPlateNumber ||
      'Vehicle'
      : '';
  const vehicleExtraCount = vehiclesList.length > 1 ? vehiclesList.length - 1 : 0;

  /**
   * Open RFID Cards side panel
   * @returns {void}
   */
  const handleOpenRfidCards = () => {
    setIsSidePanelOpen(true);
    setSidePanelData({
      title: t('rfidCards', 'RFID Cards'),
      content: <RfidCard />,
    });
  };

  /**
   * Open Personal Chargers side panel
   * @returns {void}
   */
  const handleOpenPersonalChargers = () => {
    setIsSidePanelOpen(true);
    setSidePanelData({
      title: t('personalCharger'),
      content: <PersonalChargers />,
    });
  };

  /**
   * Open Favorite Stations side panel
   * @returns {void}
   */
  const handleOpenFavoriteStations = () => {
    setIsSidePanelOpen(true);
    setSidePanelData({
      title: t('favoriteStations'),
      content: <MapSidePanel />,
    });
  };

  /**
   * Open Payment Method side panel
   * @returns {void}
   */
  const handleOpenPaymentMethod = () => {
    setIsSidePanelOpen(true);
    setSidePanelData({
      title: t('paymentMethod'),
      content: <PaymentMethod />,
    });
  };

  /**
   * Open Vehicle Details side panel
   * @returns {void}
   */
  const handleOpenVehicleDetails = () => {
    setIsSidePanelOpen(true);
    setSidePanelData({
      title: t('vehicle'),
      content: <MyVehicle />,
    });
  };

  const statsConfig: Array<{
    key: string;
    label: string;
    value: React.ReactNode;
    unit: string;
    icon: React.ReactNode;
    theme: string;
    isClickable?: boolean;
    onClick?: () => void;
  }> = [
    {
      key: 'energy',
      label: t('lifetimeEnergyConsumption'),
      value: ToFixedValue(statsData?.totalPowerConsumedKw ?? statsData?.totalEnergyDelivered ?? 0, 3) || '0',
      unit: 'kW',
      icon: <FleshIcon />,
      theme: 'energy',
      isClickable: false,
    },
    ...spendCards.map((item) => ({ ...item, isClickable: false })),
    {
      key: 'sessions',
      label: t('totalSessions', { defaultValue: 'Total Sessions' }),
      value: ToFixedValue(statsData?.totalSessions ?? statsData?.totalSessionCount ?? 0, 0) || '0',
      unit: '',
      icon: <CalenderIcon />,
      theme: 'sessions',
      isClickable: false,
    },
    {
      key: 'duration',
      label: t('totalDuration', { defaultValue: 'Total Duration' }),
      value: formatLifetimeDuration(
        statsData?.totalDuration ??
          statsData?.totalDurationSeconds ??
          statsData?.durationSeconds ??
          statsData?.duration,
        statsData?.totalDurationInMinutes,
      ),
      unit: '',
      icon: <TimerIcon />,
      theme: 'duration',
      isClickable: false,
    },
    {
      key: 'rfid',
      label: t('rfidNo'),
      value: totalRfidCount || '-',
      unit: '',
      icon: <RfidIcon />,
      theme: 'rfid',
      isClickable: Boolean(totalRfidCount),
      onClick: totalRfidCount ? handleOpenRfidCards : undefined,
    },
    {
      key: 'charger',
      label: t('personalCharger'),
      value: personalChargerCount || '-',
      unit: '',
      icon: <ChargerIcon />,
      theme: 'charger',
      isClickable: Boolean(personalChargerCount),
      onClick: personalChargerCount ? handleOpenPersonalChargers : undefined,
    },
    {
      key: 'favoriteStations',
      label: t('favoriteStations'),
      value: totalFavoriteCount || '-',
      unit: '',
      icon: <FavoriteIcon />,
      theme: 'favorite',
      isClickable: Boolean(totalFavoriteCount),
      onClick: totalFavoriteCount ? handleOpenFavoriteStations : undefined,
    },
    {
      key: 'paymentMethod',
      label: t('paymentMethod'),
      value: (
        <span className="stat-card-inline-value">
          <span className="main-text">{paymentMethodDisplay}</span>
          {paymentExtraCount > 0 && <span className="counts">+{paymentExtraCount}</span>}
        </span>
      ),
      unit: '',
      icon: <CardIcon />,
      theme: 'payment',
      isClickable: Boolean(paymentMethodDisplay),
      onClick: paymentMethodDisplay ? handleOpenPaymentMethod : undefined,
    },
    {
      key: 'vehicle',
      label: t('vehicle'),
      value: firstVehicleName ? (
        <span className="stat-card-inline-value">
          <span className="main-text">{firstVehicleName}</span>
          {vehicleExtraCount > 0 && <span className="counts">+{vehicleExtraCount}</span>}
        </span>
      ) : (
        '-'
      ),
      unit: '',
      icon: <CarIcon />,
      theme: 'vehicle',
      isClickable: vehiclesList.length > 0,
      onClick: vehiclesList.length > 0 ? handleOpenVehicleDetails : undefined,
    },
  ];

  const bottomContainerTabs: TabItem[] =
    consumerType === 'commercial'
      ? [
        {
          label: t('activeSession'),
          key: 'activeSession',
          isActive: bottomActiveTab === 'activeSession',
        },
        {
          label: t('chargeSession'),
          key: 'chargeSession',
          isActive: bottomActiveTab === 'chargeSession',
        },
        {
          label: t('transactionHistory'),
          key: 'transactionHistory',
          isActive: bottomActiveTab === 'transactionHistory',
        },
        {
          label: t('paymentHistory', { defaultValue: 'Payment History' }),
          key: 'paymentHistory',
          isActive: bottomActiveTab === 'paymentHistory',
        },
        {
          label: t('invoice'),
          key: 'invoice',
          isActive: bottomActiveTab === 'invoice',
        },
        // {
        //   label: t('reservations'),
        //   key: 'reservations',
        //   isActive: bottomActiveTab === 'reservations',
        // },
        // {
        //   label: t('guestCharging'),
        //   key: 'guestCharging',
        //   isActive: bottomActiveTab === 'guestCharging',
        // },
      ]
      : [
        {
          label: t('activeSession'),
          key: 'activeSession',
          isActive: bottomActiveTab === 'activeSession',
        },
        {
          label: t('schedule'),
          key: 'schedule',
          isActive: bottomActiveTab === 'schedule',
        },
        {
          label: t('chargeSession'),
          key: 'chargeSessionPersonal',
          isActive: bottomActiveTab === 'chargeSessionPersonal',
        },
        {
          label: t('paymentHistory', { defaultValue: 'Payment History' }),
          key: 'paymentHistory',
          isActive: bottomActiveTab === 'paymentHistory',
        },
      ];

  /**
   * Switch the left column tab (Basic Info / Notifications / OTPs)
   * @param {string} key - The tab key
   */
  const handleLeftSwitchTab = (key: string) => {
    setLeftActiveTab(key);
  };

  /**
   * Switch the right column tab (Total Consumption / Spend / Sessions Count / Lifetime)
   * @param {string} key - The tab key
   */
  const handleRightSwitchTab = (key: string) => {
    setRightActiveTab(key);
  };

  /**
   * Switch the bottom container tab (Charge Session / Schedule / Reservations / Guest Charging / Transaction History)
   * @param {string} key - The tab key
   */
  const handleBottomSwitchTab = (key: string) => {
    setBottomActiveTab(key);
  };

  // /**
  //  * Switch the consumer type
  //  * @param {string} name - The consumer type
  //  * @param {string} value - The consumer type value
  //  */
  // const handleConsumerTypeChange = (name: string, value: string) => {
  //   setConsumerType(value);
  // };

  /**
   * Fetching card list api
   * @param {any} consumerId - The consumerId
   */
  const handleNotificationsCard = async (consumerId: string) => {
    try {
      const response = await endConsumerApi.fetchNotifications(consumerId);

      setNotifications(response?.data || []);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Fetch lifetime power and spend stats for the selected end consumer
   * @param {string} consumerId - The driver or end consumer custom ID
   * @returns {Promise<void>}
   */
  const handleFetchDriverStats = async (consumerId: string): Promise<void> => {
    try {
      const response = await endConsumerApi.fetchDriverPowerSpendStats(consumerId);
      const resourceData = response?.data;
      let payload = resourceData ?? null;
      const isTopLevel =
        resourceData &&
        !Array.isArray(resourceData) &&
        ('totalPowerConsumedKw' in resourceData || 'totalSessions' in resourceData);

      if (!isTopLevel && resourceData?.data && !Array.isArray(resourceData.data)) {
        payload = resourceData.data;
      }

      setDriverStats(payload);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Fetching Card OTP function
   * @param {any} consumerId - The consumerId
   */
  const handleFetchOtp = async (consumerId: string) => {
    try {
      const response = await endConsumerV2Api.fetchOtps(consumerId);

      setOtps(response?.data || []);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Fetch favorite stations count for customer
   * @param {string} consumerId - The consumer ID
   * @returns {Promise<void>}
   */
  const handleFetchFavoriteStations = async (consumerId: string): Promise<void> => {
    try {
      const response = await endConsumerApi.fetchFavoriteStations(consumerId);
      setFavoriteStationCount(response?.data?.results?.length || 0);
    } catch (error) {
      console.error('Failed to fetch favorite stations', error);
    }
  };

  /**
   * Fetch RFID tokens count for customer
   * @param {string} consumerId - The consumer ID
   * @returns {Promise<void>}
   */
  const handleFetchRfidTokens = async (consumerId: string): Promise<void> => {
    try {
      const response = await rfidApi.fetchTokensByCustomer(consumerId);
      const results =
        response?.data?.results ||
        response?.data?.data ||
        (Array.isArray(response?.data) ? response.data : []);
      setRfidCardsCount(results.length);
    } catch (error) {
      console.error('Failed to fetch RFID tokens', error);
    }
  };

  /**
   * Navigate back to the source page or previous route dynamically
   */
  const handleNavigateBack = () => {
    if (location.state?.source) {
      navigate(location.state.source);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    // Run only for profile page
    if (!pathname.includes('/end-consumer-v2/profile')) return;

    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('.ev-content');

      if (scrollContainer instanceof HTMLElement) {
        scrollContainer.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
    });
  }, []);

  useEffect(() => {
    dispatch(
      setSelectedEndConsumer({
        userCustomId,
      }),
    );
    dispatch(
      setEndConsumerData({
        name: 'endConsumerType',
        value: type || '',
      }),
    );
  }, [userCustomId, type]);

  useEffect(() => {
    const id = userCustomId || selectedEndConsumer?.userCustomId;
    if (id) {
      dispatch(fetchBasicInformations({ userCustomId: id }));
      handleNotificationsCard(id);
      handleFetchOtp(id);
      handleFetchDriverStats(id);
      handleFetchFavoriteStations(id);
      handleFetchRfidTokens(id);
    }
  }, [userCustomId, selectedEndConsumer?.userCustomId]);
  return (
    <div className="end-consumers-container" dir={i18n.dir()}>
      {isSidePanelOpen && (
        <SidePaneModel
          open={isSidePanelOpen}
          title={sidePanelData.title}
          content={sidePanelData.content}
          closePanel={() => setIsSidePanelOpen(false)}
          width={sidePanelData.width}
        />
      )}

      <div className="ec-profile-top-grid">
        {/* Left Column: Basic Info / Notifications / OTPs */}
        <div className="ec-profile-left-column">
          <div className="left-column-header">
            <div className="end-consumer-profile-header">
              <div className="navigate-arrow" role="presentation" onClick={handleNavigateBack}>
                <span>
                  <NavigationArrowIcon className="icon" />
                </span>
                <div className="navigate-background" />
              </div>
            </div>
            <div className="tab-switch-container">
              <TabSwitchList tabs={leftContainerTabs} onSwitchTab={handleLeftSwitchTab} />
            </div>
          </div>

          <div className="left-tab-content">
            {leftActiveTab === 'basicInfo' && <ProfileConsumer />}
            {leftActiveTab === 'notifications' && (
              <div className="menu-tab-container">
                <div className="menu-tab-body">
                  {notifications?.length > 0 ? (
                    notifications.map((item: NotificationItem) => (
                      <div key={`${item.title}-${item.createdAt}`} className="menu-tab-row">
                        <span className="title">{item.title}</span>
                        <span className="time">{ConvertUTCtoDateTime(item.createdAt)}</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">{t('noNotificationsFound')}</p>
                  )}
                </div>
              </div>
            )}
            {leftActiveTab === 'otps' && (
              <div className="menu-tab-container">
                <div className="menu-tab-body">
                  {otps?.length > 0 ? (
                    [...otps]
                      .sort(
                        (a: OtpItemInterface, b: OtpItemInterface) =>
                          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                      )
                      .map((item: OtpItemInterface) => (
                        <div key={`${item.otp}-${item.createdAt}`} className="menu-tab-row">
                          <span className="title">{item.otp}</span>
                          <span className="otp-type">{item.type}</span>
                          <span className="time">{ConvertUTCtoDateTime(item.createdAt)}</span>
                        </div>
                      ))
                  ) : (
                    <p className="no-data">{t('noOtpsFound')}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Total Consumption / Spend / Sessions Count */}
        <div className="ec-profile-right-column">
          <div className="tab-switch-container">
            <TabSwitchList tabs={rightContainerTabs} onSwitchTab={handleRightSwitchTab} />
          </div>

          <div className="right-tab-content">
            {rightActiveTab === 'totalConsumption' && <TotalConsumptionChart />}
            {rightActiveTab === 'spend' && <SpendChart />}
            {rightActiveTab === 'sessionsCount' && <SessionsCountChart />}
            {rightActiveTab === 'lifetime' && (
              <div className="stat-card-content">
                {statsConfig.map((item) => (
                  <div
                    key={item.key}
                    className={`stat-card ${item.theme ? `stat-card--${item.theme}` : ''} ${item.isClickable ? 'stat-card--clickable' : ''
                      }`}
                    role="presentation"
                    onClick={item.onClick}>
                    <div className="icon-wrapper">{item.icon}</div>

                    <div className="content">
                      <p className="label">{item.label}</p>

                      <div className="value">
                        {item.value}
                        {item.unit && <span className="unit"> {item.unit}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bottom-line" />

      <div className="table-list">
        {/* <div className="consumer-type-toggles-row">
          <CustomRadioButton
            selected={consumerType === 'commercial'}
            label={t('commercial')}
            value="commercial"
            name="consumerType"
            onChangeFunc={handleConsumerTypeChange}
          />
          <CustomRadioButton
            selected={consumerType === 'personal'}
            label={t('personal')}
            value="personal"
            name="consumerType"
            onChangeFunc={handleConsumerTypeChange}
          />
        </div> */}
        <TabSwitchList tabs={bottomContainerTabs} onSwitchTab={handleBottomSwitchTab} />
        {/* Renders ONLY when bottomActiveTab is 'chargeSession' (Commercial) */}
        {bottomActiveTab === 'chargeSession' && (
          <div className="table-data-list">
            <ChargeSessionCommercialList />
          </div>
        )}
        {/* Renders ONLY when bottomActiveTab is 'chargeSessionPersonal' (Personal) */}
        {bottomActiveTab === 'chargeSessionPersonal' && (
          <div className="table-data-list">
            <ChargeSessionPersonalList />
          </div>
        )}
        {bottomActiveTab === 'activeSession' && (
          <div className="table-data-list">
            <ActiveChargingSession isSplitView />
          </div>
        )}
        {bottomActiveTab === 'schedule' && (
          <div className="table-data-list">
            <TablesToggle
              tabs={[
                { label: t('upcoming'), content: <ScheduleUpcomingList /> },
                { label: t('completed'), content: <ScheduleCompletedList /> },
              ]}
              defaultActive={t('upcoming')}
            />
          </div>
        )}
        {bottomActiveTab === 'invoice' && (
          <div className="table-data-list">
            <InvoiceList />
          </div>
        )}
        {bottomActiveTab === 'reservations' && (
          <div className="table-data-list">
            <ReservationsList />
          </div>
        )}

        {bottomActiveTab === 'guestCharging' && (
          <div className="table-data-list">
            <GuestChargingList />
          </div>
        )}
        {bottomActiveTab === 'transactionHistory' && (
          <div className="table-data-list">
            <TransactionHistoryList />
          </div>
        )}
        {bottomActiveTab === 'paymentHistory' && (
          <div className="table-data-list">
            <PaymentHistoryList />
          </div>
        )}
      </div>
    </div>
  );
}

export default EndConsumersProfile;
